/**
 * UAE-3 — write a user-authored claim from the Coverage Matrix
 * `[Author]` dialog into Firestore + Zustand.
 *
 * Flow:
 *   1. AuthorEvidenceDialog collects form values per the
 *      `ChecklistItem.authoredSchema` for the requirement.
 *   2. Dialog calls `writeAuthoredClaim(siteId, requirementId, form,
 *      schema)` from its Save handler.
 *   3. Writer builds an `ExtractedClaim` with `source: 'user-typed'`,
 *      `requirementId`, `authoredKind`, `structuredFields`, and a
 *      composed `claim` text (used by the LLM prompt block and the
 *      cross-checker).
 *   4. Writer hands the claim to `mergeClaimsIntoSiteContext` with
 *      `replaceByRequirementId: true` so a re-author of the same
 *      requirement replaces the prior version (archived onto
 *      `priorVersions`).
 *   5. Server authorizes the owner/site and compares the current evidence
 *      basis inside the Firestore transaction. Zustand updates only after
 *      a matching acknowledgement; the canonical SED awaits normal rebuild.
 *
 * Pure-ish — the Firebase imports are dynamic so test environments can
 * stub them. The writer is itself a free function (no React) so it can
 * be unit-tested without rendering the dialog.
 */
import type { ExtractedClaim, ClaimCategory, SiteContext } from '../../types/siteContext';
import type { AuthoredEvidenceFormState, AuthoredFieldDef } from '../../types/authoredEvidence';
import {
  confidenceToScore,
  hasAnyStructuredField,
  isAuthoredValuePresent,
  isFieldFilled,
} from '../../types/authoredEvidence';
import { emptySiteContext } from '../../types/siteContext';
import { mergeClaimsIntoSiteContext } from './mergeClaimsIntoSiteContext';
import { bindsToSedPathsForAuthoredClaim } from '../../lib/coverage/authoredSedOverlay';
import { checklistItemById } from '../../config/documentChecklists';
import { authedFetch } from '../../lib/authedFetch';
import { hashCanonicalJson } from '../../lib/reportGeneration/reportContractHash';
import { stripUndefined as stripForPersistence } from '../../lib/firestore/stripUndefined';
import { siteOwnerUid } from '../../lib/siteOwnerPath';
import { authoredClaimBasisHash, authoredClaimSchemaHash, authoredClaimSaveAckSchema, authoredClaimSaveRequestSchema } from '../../lib/coverage/authoredClaimPersistence';

/**
 * Map a Coverage Matrix `ChecklistItem.id` to the `ClaimCategory` the
 * authored claim should be filed under. The category drives the
 * cross-checker comparator selection (claims with an unknown category
 * fall back to `'general'`). Add new requirement → category mappings
 * here when introducing more `ChecklistItem` ids; the default
 * `'general'` keeps the writer safe for unmapped requirements.
 */
const REQUIREMENT_TO_CATEGORY: Record<string, ClaimCategory> = {
  geotech: 'geotechnical.foundation',
  'utility-letter': 'power.grid',
  'parcel-survey': 'parcel.boundaries',
  zoning: 'regulatory.zoning',
  'flood-cert': 'parcel.boundaries',
  'phase1-esa': 'environmental.phase1',
  wetland: 'environmental.wetlands',
  'species-survey': 'environmental.species',
  cultural: 'environmental.nepa',
  'iso-queue': 'power.timeline',
  tariff: 'finance.ppa',
  'water-rights': 'water.rights',
  'aquifer-study': 'water.source',
  'discharge-permit': 'water.source',
  survey: 'parcel.acres',
  'utility-locate': 'parcel.boundaries',
  'fiber-survey': 'connectivity.fiber',
  'latency-test': 'connectivity.latency',
  'btm-vendor-quote': 'power.btm',
  'vendor-quote-pack': 'general',
  'community-feedback-summary': 'general',
  'diligence-source-pack': 'general',
  'prior-report-pack': 'general',
};

export function inferCategoryFromRequirement(requirementId: string): ClaimCategory {
  return REQUIREMENT_TO_CATEGORY[requirementId] ?? 'general';
}

/**
 * Compose the human-readable claim text the LLM prompt sees. Joins the
 * populated structured fields with their declared labels + units, then
 * appends the free-text notes. Mirrors the shape `extractClaims` would
 * produce for a structured ground-truth statement.
 *
 * Example output:
 *   "Bearing capacity: 200 kPa; Soil classification: SC; Borehole depth: 30 m.
 *    Notes: verified verbally with consultant X 2026-05-17."
 *
 * When no structured fields are filled the function returns the notes
 * verbatim (prose-only authoring). When neither structured nor prose is
 * supplied the function returns a single-sentence placeholder so the
 * claim isn't empty in the prompt — the writer rejects this case at
 * the call site so it should never actually materialise.
 */
export function composeClaimText(
  requirementLabel: string,
  schema: readonly AuthoredFieldDef[],
  form: AuthoredEvidenceFormState,
): string {
  const parts: string[] = [];
  for (const field of schema) {
    if (field.key === 'notes') continue;
    const value = form.fields[field.key];
    if (!isFieldFilled(value)) continue;
    const unit = field.unit ? ` ${field.unit}` : '';
    parts.push(`${field.label}: ${value}${unit}`);
  }
  const notes = (form.notes ?? '').trim();
  const head = parts.length > 0 ? parts.join('; ') + '.' : '';
  const tail = notes ? `Notes: ${notes}` : '';
  const body = [head, tail].filter(s => s.length > 0).join(' ');
  if (body.length === 0) {
    return `User-authored evidence for "${requirementLabel}" (no fields filled).`;
  }
  return body;
}

/** UAE-3 — generate a stable id for a fresh authored claim. */
export function makeClaimId(): string {
  // Mirrors the id format used elsewhere in the codebase (`c-<rand>`).
  return `c-${Math.random().toString(36).slice(2, 10)}-${Date.now().toString(36)}`;
}

/**
 * Result returned from `buildAuthoredClaim` — pulled out as a pure
 * helper so tests can verify the shape without doing IO.
 */
export interface BuildAuthoredClaimInputs {
  requirementId: string;
  requirementLabel: string;
  schema: readonly AuthoredFieldDef[];
  form: AuthoredEvidenceFormState;
  /** Authenticated user record (`updatedBy` on ExtractedClaim). */
  author?: { uid: string; email?: string };
  /** Stable id — generated when omitted (lets tests pin the id). */
  claimId?: string;
  /** ISO timestamp — defaults to `new Date().toISOString()` so tests can pin. */
  nowIso?: string;
}

/**
 * Pure assembler — build the `ExtractedClaim` payload for an authored
 * write without doing any IO. Exposed for unit tests.
 */
export function buildAuthoredClaim(inputs: BuildAuthoredClaimInputs): ExtractedClaim {
  const {
    requirementId,
    requirementLabel,
    schema,
    form,
    author,
    claimId = makeClaimId(),
    nowIso = new Date().toISOString(),
  } = inputs;

  // Strip empty / blank values from structuredFields so Firestore
  // doesn't carry empty strings around — keeps diffs tidy and lets
  // `isFieldFilled` callers downstream behave consistently.
  const structuredFields: Record<string, string | number | boolean> = {};
  for (const field of schema) {
    if (field.key === 'notes') continue;
    const value = form.fields[field.key];
    if (!isAuthoredValuePresent(value)) continue;
    structuredFields[field.key] = value;
  }

  const structured = hasAnyStructuredField(form, schema);
  const claimText = composeClaimText(requirementLabel, schema, form);

  const claim: ExtractedClaim = {
    id: claimId,
    category: inferCategoryFromRequirement(requirementId),
    sourceSpan: claimText,
    claim: claimText.slice(0, 120),
    extractorConfidence: confidenceToScore(form.confidence),
    source: 'user-typed',
    requirementId,
    authoredKind: structured ? 'structured' : 'prose',
    structuredFields,
    bindsToSedPaths: bindsToSedPathsForAuthoredClaim({ requirementId, structuredFields }),
    lastWrittenAt: nowIso,
    lastWrittenBy: author,
    versionNote: `User authored via Coverage Matrix at ${nowIso}`,
  };
  return claim;
}

/**
 * Confidence floor stamped on a claim a user manually corrects via the
 * inline editor in the Extracted-claims list. On the SC3 0-100 scale
 * (the scale SC3-extracted claims carry), `100` is the established
 * "user-authored, ground truth" value — it matches
 * `operatorAttestationWriter` (`extractorConfidence: 100`) and
 * `legacyDescriptionMigration` (`extractorConfidence: 100 // user-
 * authored, treated as ground truth`). Keeping the same scale means the
 * cross-checker, confidence-tier, and prompt-block reads all treat a
 * corrected claim as HIGH-TRUST without a per-reader special case.
 */
export const USER_CORRECTED_CLAIM_CONFIDENCE = 100;

/**
 * Inputs for {@link promoteClaimToUserAuthored} — the inline-edit patch.
 * Every field is optional except the original claim; the editor passes
 * only the parts the user actually touched (text / value / category).
 */
export interface PromoteClaimEditInputs {
  /** The original extracted claim being corrected. */
  claim: ExtractedClaim;
  /** Corrected claim text (`ExtractedClaim.claim`). */
  claim_text?: string;
  /**
   * Corrected value. `undefined` ⇒ the user left the value control
   * untouched; pass an explicit `null` to CLEAR a previously-set value.
   */
  claimedValue?: string | number | boolean | null;
  /** Corrected category, when the user re-filed the claim. */
  category?: ClaimCategory;
  /** Authenticated user (audit metadata). */
  author?: { uid: string; email?: string };
  /** ISO timestamp — defaults to now; pinnable for tests. */
  nowIso?: string;
}

/**
 * UAE — promote an SC3-extracted claim the user has inline-corrected to
 * a USER-AUTHORED, high-trust claim. Reuses the existing UAE markers so
 * downstream (SED sync, verdict, prompt block, confidence-tier) treats
 * the corrected claim as ground truth rather than a low-confidence
 * extraction:
 *
 *   - `source: 'user-typed'`        — provenance is now the human.
 *   - `state: 'authored'`           — the SCE-3 "user authored this claim
 *                                     directly" marker. `reportPrompts`
 *                                     keys on this to admit otherwise-
 *                                     gated claims (e.g. image
 *                                     observations) into the narrative.
 *   - `extractorConfidence: 100`    — ground-truth confidence on the SC3
 *                                     0-100 scale (see
 *                                     `USER_CORRECTED_CLAIM_CONFIDENCE`).
 *   - `qualityWarnings` cleared     — the amber extractor warnings no
 *                                     longer apply once a human owns the
 *                                     value; leaving them would mis-flag
 *                                     a corrected claim as suspect.
 *   - `lastWrittenBy/At`, `versionNote` — audit trail.
 *
 * Pure — no IO, no React. The caller persists the resulting claims array
 * via `onClaimsChange` (the same contract Delete already uses).
 */
export function promoteClaimToUserAuthored(inputs: PromoteClaimEditInputs): ExtractedClaim {
  const { claim, claim_text, claimedValue, category, author, nowIso = new Date().toISOString() } = inputs;

  const nextText = claim_text !== undefined ? claim_text.trim() : claim.claim;

  // Resolve the corrected value:
  //   - `undefined`  ⇒ keep the original value untouched.
  //   - `null`       ⇒ explicitly clear it.
  //   - otherwise    ⇒ adopt the new value.
  let nextValue: string | number | boolean | undefined;
  if (claimedValue === undefined) {
    nextValue = claim.claimedValue;
  } else if (claimedValue === null) {
    nextValue = undefined;
  } else {
    nextValue = claimedValue;
  }

  const corrected: ExtractedClaim = {
    ...claim,
    category: category ?? claim.category,
    claim: (nextText || claim.claim).slice(0, 120),
    claimedValue: nextValue,
    // Mark user-authored / ground-truth via the existing UAE markers.
    source: 'user-typed',
    state: 'authored',
    extractorConfidence: USER_CORRECTED_CLAIM_CONFIDENCE,
    lastWrittenAt: nowIso,
    lastWrittenBy: author ?? claim.lastWrittenBy,
    versionNote: `User-corrected extracted claim at ${nowIso}`,
  };

  // The extractor quality warnings no longer apply once a human owns the
  // value — strip them so the corrected claim isn't mis-flagged as a
  // suspect extraction downstream. (`delete` keeps the field absent
  // rather than carrying an empty array.)
  delete corrected.qualityWarnings;

  return corrected;
}

/**
 * Build the next SiteContext after merging the authored claim. Pure —
 * exposed for tests; the live `writeAuthoredClaim` calls this then
 * persists.
 */
export function applyAuthoredClaimToContext(
  prior: SiteContext | undefined,
  claim: ExtractedClaim,
): SiteContext {
  const base = prior ?? emptySiteContext();
  const merged = mergeClaimsIntoSiteContext(base, [claim], {
    provenance: 'user-typed',
    replaceByRequirementId: true,
  });
  return {
    ...base,
    claims: merged.claims,
    updatedAt: claim.lastWrittenAt ?? new Date().toISOString(),
    updatedBy: claim.lastWrittenBy ?? base.updatedBy,
  };
}

/** Inputs for the live writer — the version that touches Zustand + Firestore. */
export interface WriteAuthoredClaimInputs extends Omit<BuildAuthoredClaimInputs, 'author'> {
  siteId: string;
}

export interface WriteAuthoredClaimResult {
  claimId: string;
  /** Updated siteContext.claims length — useful for telemetry callers. */
  claimCount: number;
  /** Canonical claim saved; the existing SED still requires its normal rebuild. */
  sedSyncStatus?: 'pending-rebuild';
}

/** Checked canonical claim save. UI/local evidence changes only after a source-bound server ACK. */
export async function writeAuthoredClaim(inputs: WriteAuthoredClaimInputs): Promise<WriteAuthoredClaimResult> {
  const { auth } = await import('../../firebase');
  const user = auth.currentUser;
  if (!user) throw new Error('Sign in before saving facts.');
  const actorUid = user.uid;
  const canonicalItem = checklistItemById(inputs.requirementId);
  if (!canonicalItem) throw new Error('The fact fields are no longer available. Reload evidence before retrying.');
  const { useStore } = await import('../../store/index');
  const site = useStore.getState().siteRag.sites.find(candidate => candidate.id === inputs.siteId);
  if (!site) throw new Error('The site is no longer loaded. Reload evidence before retrying.');
  const request = authoredClaimSaveRequestSchema.parse({ ownerUid: siteOwnerUid(site, actorUid), siteId: site.id,
    operationId: crypto.randomUUID(), requirementId: inputs.requirementId,
    expectedBasisHash: authoredClaimBasisHash(site), schemaHash: authoredClaimSchemaHash(inputs.schema), form: inputs.form });
  let response: Response;
  try {
    response = await authedFetch('/api/site-authored-claims', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(request) });
  } catch { throw new Error('The fact save could not be confirmed. Reload evidence before retrying.'); }
  const raw: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const message = raw && typeof raw === 'object' && 'error' in raw && typeof raw.error === 'string' ? raw.error
      : 'The fact save could not be confirmed. Reload evidence before retrying.';
    throw new Error(message);
  }
  const parsed = authoredClaimSaveAckSchema.safeParse(raw);
  if (!parsed.success) throw new Error('The fact save could not be confirmed. Reload evidence before retrying.');
  const ack = parsed.data;
  if (ack.ownerUid !== request.ownerUid || ack.siteId !== request.siteId || ack.operationId !== request.operationId
    || ack.expectedBasisHash !== request.expectedBasisHash || ack.author.uid !== actorUid || ack.claimId !== `c-${request.operationId}`)
    throw new Error('The save acknowledgement does not match these facts. Reload evidence before retrying.');
  const claim = buildAuthoredClaim({ requirementId: inputs.requirementId, requirementLabel: canonicalItem.label,
    schema: inputs.schema, form: inputs.form, claimId: ack.claimId, nowIso: ack.writtenAt, author: ack.author });
  const nextContext = stripForPersistence(applyAuthoredClaimToContext(site.siteContext, claim));
  if (ack.contextHash !== hashCanonicalJson(nextContext) || ack.claimCount !== nextContext.claims.length)
    throw new Error('The saved facts could not be reconciled. Reload evidence before retrying.');
  let committed = false;
  useStore.setState(state => ({ siteRag: { ...state.siteRag, sites: state.siteRag.sites.map(current => {
    if (current.id !== site.id) return current;
    if (auth.currentUser?.uid !== actorUid || siteOwnerUid(current, actorUid) !== request.ownerUid
      || (authoredClaimBasisHash(current) !== request.expectedBasisHash
        && authoredClaimBasisHash(current) !== ack.afterBasisHash)) return current;
    committed = true;
    // The canonical claim is now durable. Existing field resolvers overlay it;
    // the unchanged SED remains its own saved provider/document source until rebuild.
    return { ...current, siteContext: nextContext, updatedAt: ack.writtenAt };
  }) } }));
  if (!committed) throw new Error('The facts were acknowledged, but the active site changed. Reload evidence before retrying.');
  try {
    const { emitAuditEvent } = await import('../../lib/auditLogClient');
    void emitAuditEvent({ action: 'site.context_authored_claim', target: { type: 'site', id: site.id, label: site.name },
      before: { claimCount: site.siteContext?.claims.length ?? 0 }, after: { claimCount: nextContext.claims.length },
      metadata: { requirementId: inputs.requirementId, authoredKind: claim.authoredKind, confidence: inputs.form.confidence } });
  } catch { /* A soft audit breadcrumb is separate from the acknowledged canonical write. */ }
  return { claimId: ack.claimId, claimCount: ack.claimCount, sedSyncStatus: ack.sedSyncStatus };
}
